import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Upload, Download, Trash2, FileText, File, CheckCircle, AlertCircle, Clock, Eye, Info } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { hasAnyRole } from '../utils/roleUtils';

interface Document {
  id: string;
  companyId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedBy: string;
  uploadedByName: string;
  uploadedAt: string;
  tags: string[];
  isViewed?: boolean; // New field for view tracking
}

interface CompanyDocumentsProps {
  accessToken: string;
  companyId: string;
  companyName: string;
  currentUser: any;
  onUnviewedCountChange?: (count: number) => void;
}

export function CompanyDocuments({
  accessToken,
  companyId,
  companyName,
  currentUser,
  onUnviewedCountChange,
}: CompanyDocumentsProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState('other');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [viewedDocs, setViewedDocs] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadDocuments();
    loadUnviewedCount();
  }, [companyId]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/documents`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      } else {
        console.error('Failed to load documents');
      }
    } catch (err) {
      console.error('Error loading documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUnviewedCount = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/documents/unviewed-count`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (onUnviewedCountChange) {
          onUnviewedCountChange(data.unviewedCount || 0);
        }
      }
    } catch (err) {
      console.error('Error loading unviewed count:', err);
    }
  };

  const markDocumentAsViewed = async (docId: string) => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/documents/${docId}/mark-viewed`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        // Add to local viewed set
        setViewedDocs(prev => new Set([...prev, docId]));
        // Refresh unviewed count
        await loadUnviewedCount();
      }
    } catch (err) {
      console.error('Error marking document as viewed:', err);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadError('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('Please select a file');
      return;
    }

    setUploading(true);
    setUploadError('');
    setUploadSuccess(false);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('documentType', documentType);

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/documents`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
          body: formData,
        }
      );

      if (response.ok) {
        setUploadSuccess(true);
        setSelectedFile(null);
        setDocumentType('other');
        setShowUploadDialog(false);
        await loadDocuments();
      } else {
        const error = await response.json();
        setUploadError(error.error || 'Failed to upload document');
      }
    } catch (err: any) {
      console.error('Error uploading document:', err);
      setUploadError('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (document: Document) => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/documents/${document.id}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Mark document as viewed
        await markDocumentAsViewed(document.id);
        // Update local document state
        setDocuments(prev => prev.map(d => 
          d.id === document.id ? { ...d, isViewed: true } : d
        ));
        // Open signed URL in new tab
        window.open(data.signedUrl, '_blank');
      } else {
        console.error('Failed to get download link');
      }
    } catch (err) {
      console.error('Error downloading document:', err);
    }
  };

  const handleDelete = async (document: Document) => {
    if (!confirm(`Are you sure you want to delete "${document.fileName}"?`)) {
      return;
    }

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/documents/${document.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        await loadDocuments();
      } else {
        console.error('Failed to delete document');
      }
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileType: string) => {
    const type = fileType.toLowerCase();
    if (type === 'pdf') return <FileText className="h-5 w-5 text-red-500" />;
    if (type === 'doc' || type === 'docx') return <FileText className="h-5 w-5 text-blue-500" />;
    if (type === 'txt') return <FileText className="h-5 w-5 text-gray-500" />;
    return <File className="h-5 w-5 text-muted-foreground" />;
  };

  const getDocumentTypeBadge = (tags: string[]) => {
    if (!tags || tags.length === 0) return null;
    
    const typeColors: Record<string, string> = {
      sow: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      hlo: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      meeting: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      technical: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      other: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
    };

    const primaryTag = tags[0];
    const colorClass = typeColors[primaryTag] || typeColors.other;

    return (
      <Badge variant="outline" className={colorClass}>
        {primaryTag.toUpperCase()}
      </Badge>
    );
  };

  const filteredDocuments = filterType === 'all' 
    ? documents 
    : documents.filter(doc => doc.tags.includes(filterType));

  const canUpload = 
    hasAnyRole(currentUser.role, ['consultant', 'cfAdmin', 'companyAdmin', 'systemAdmin']);

  return (
    <div className="space-y-6">
      {uploadSuccess && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-800 dark:text-green-200">
            Document uploaded.
          </AlertDescription>
        </Alert>
      )}

      <Alert className="border-blue-500 bg-blue-50 dark:bg-blue-950">
        <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <AlertDescription className="text-blue-800 dark:text-blue-200">
          Store and manage all project documents including SOWs, HLO summaries, meeting notes, and technical specifications. All documents are securely stored and accessible to authorized team members.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Documents</CardTitle>
              <CardDescription>
                Manage documents for {companyName}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Documents</SelectItem>
                  <SelectItem value="sow">SOW</SelectItem>
                  <SelectItem value="hlo">HLO Summary</SelectItem>
                  <SelectItem value="meeting">Meeting Notes</SelectItem>
                  <SelectItem value="technical">Technical Specs</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              {canUpload && (
                <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
                  <DialogTrigger asChild>
                    <Button>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Document
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Upload Document</DialogTitle>
                      <DialogDescription>
                        Upload a document for {companyName}
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                      {uploadError && (
                        <Alert variant="destructive">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>{uploadError}</AlertDescription>
                        </Alert>
                      )}

                      <div className="space-y-2">
                        <Label htmlFor="document-type">Document Type</Label>
                        <Select value={documentType} onValueChange={setDocumentType}>
                          <SelectTrigger id="document-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sow">Statement of Work (SOW)</SelectItem>
                            <SelectItem value="hlo">HLO Summary</SelectItem>
                            <SelectItem value="meeting">Meeting Notes</SelectItem>
                            <SelectItem value="technical">Technical Specifications</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="file-upload">Select File</Label>
                        <Input
                          id="file-upload"
                          type="file"
                          accept=".pdf,.doc,.docx,.txt"
                          onChange={handleFileSelect}
                        />
                        {selectedFile && (
                          <p className="text-sm text-muted-foreground">
                            Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                          </p>
                        )}
                      </div>

                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowUploadDialog(false);
                            setSelectedFile(null);
                            setUploadError('');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button onClick={handleUpload} disabled={!selectedFile || uploading}>
                          {uploading ? 'Uploading...' : 'Upload'}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              <Clock className="h-8 w-8 mx-auto mb-2 animate-pulse" />
              Loading documents...
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No documents found</p>
              {canUpload && (
                <p className="mt-2">Click "Upload Document" to add your first document</p>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]"></TableHead>
                  <TableHead>File Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Uploaded By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDocuments.map((doc) => (
                  <TableRow key={doc.id} className={!doc.isViewed ? 'bg-blue-50/50' : ''}>
                    <TableCell>{getFileIcon(doc.fileType)}</TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {doc.fileName}
                        {!doc.isViewed && (
                          <Badge variant="destructive" className="text-xs px-1.5 py-0.5">
                            NEW
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getDocumentTypeBadge(doc.tags)}</TableCell>
                    <TableCell>{formatFileSize(doc.fileSize)}</TableCell>
                    <TableCell>{doc.uploadedByName}</TableCell>
                    <TableCell>
                      {new Date(doc.uploadedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(doc)}
                          className="h-8 w-8 p-0"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {canUpload && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(doc)}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}